import { useState, useEffect } from 'react';
import { Plus, Trash2, Package, Eye } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { useJobSampleRequirements } from '@/hooks/useJobSampleRequirements';
import { useDeliveryTemplates } from '@/hooks/useDeliveryTemplates';
import { DeliveryLocationTemplate } from '@/lib/types/job';
import { AddressDialog } from '@/components/addresses/AddressDialog';

interface JobSpecification {
  id: string;
  specification_name: string;
}

export interface SampleProofRow {
  id?: string;
  item_type: 'proofs' | 'samples' | '';
  specification_id: string;
  quantity: number | '';
  delivery_template_id: string;
  attention_of: string;
}

interface SamplesProofsEditorProps {
  jobId: string;
  specifications: JobSpecification[];
  readOnly?: boolean;
  onChange?: (rows: SampleProofRow[]) => void;
}

export function SamplesProofsEditor({ jobId, specifications, readOnly = false, onChange }: SamplesProofsEditorProps) {
  const { requirements, loading } = useJobSampleRequirements(jobId);
  const { templates, refetch: refetchTemplates } = useDeliveryTemplates();
  
  const [localRows, setLocalRows] = useState<SampleProofRow[]>([]);
  const [addressDialogOpen, setAddressDialogOpen] = useState(false);
  const [addressDialogMode, setAddressDialogMode] = useState<'view' | 'edit' | 'create'>('create');
  const [viewAddressDialogOpen, setViewAddressDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<DeliveryLocationTemplate | null>(null);

  // Sync requirements to local state (only on initial load for read-only, or when loading existing data)
  useEffect(() => {
    if (!loading) {
      if (requirements.length === 0 && !readOnly) {
        // Start with one empty row if editable and no existing requirements
        const emptyRow = createEmptyRow();
        setLocalRows([emptyRow]);
        onChange?.([emptyRow]);
      } else if (requirements.length > 0) {
        const mappedRows = requirements.map(req => ({
          id: req.id,
          item_type: req.item_type,
          specification_id: req.specification_id,
          quantity: req.quantity,
          delivery_template_id: req.delivery_template_id || '',
          attention_of: req.attention_of || '',
        }));
        setLocalRows(mappedRows);
        onChange?.(mappedRows);
      }
    }
  }, [requirements, loading, readOnly]);

  function createEmptyRow(): SampleProofRow {
    return {
      item_type: '',
      specification_id: '',
      quantity: '',
      delivery_template_id: '',
      attention_of: '',
    };
  }

  const handleAddRow = () => {
    const newRows = [...localRows, createEmptyRow()];
    setLocalRows(newRows);
    onChange?.(newRows);
  };

  const handleRemoveRow = (index: number) => {
    const newRows = localRows.filter((_, i) => i !== index);
    setLocalRows(newRows);
    onChange?.(newRows);
  };

  const handleFieldChange = (
    index: number,
    field: keyof SampleProofRow,
    value: string | number
  ) => {
    const newRows = localRows.map((r, i) => (i === index ? { ...r, [field]: value } : r));
    setLocalRows(newRows);
    onChange?.(newRows);
  };

  const handleOpenAddressDialog = () => {
    setAddressDialogMode('create');
    setAddressDialogOpen(true);
  };

  const handleAddressDialogClose = (open: boolean) => {
    setAddressDialogOpen(open);
    if (!open) {
      refetchTemplates();
    }
  };

  const getSpecificationName = (specId: string) => {
    const spec = specifications.find(s => s.id === specId);
    return spec?.specification_name || 'Unknown';
  };

  const getTemplateName = (templateId: string) => {
    const template = templates.find(t => t.id === templateId);
    return template?.location_name || 'Unknown';
  };

  const handleViewAddress = (templateId: string) => {
    const template = templates.find(t => t.id === templateId);
    if (template) {
      setSelectedTemplate(template);
      setViewAddressDialogOpen(true);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            Samples and Proofs
            <Badge className="bg-orange-500/15 text-orange-600 border-orange-500/20">Optional</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-muted-foreground">Loading...</div>
        </CardContent>
      </Card>
    );
  }

  // Read-only view
  if (readOnly) {
    if (requirements.length === 0) {
      return (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Package className="h-5 w-5" />
              Samples and Proofs
              <Badge className="bg-orange-500/15 text-orange-600 border-orange-500/20">Optional</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">No sample or proof requirements defined.</p>
          </CardContent>
        </Card>
      );
    }

    return (
      <>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Package className="h-5 w-5" />
              Samples and Proofs
              <Badge className="bg-orange-500/15 text-orange-600 border-orange-500/20">Optional</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Item</TableHead>
                  <TableHead>Specification</TableHead>
                  <TableHead>Quantity</TableHead>
                  <TableHead>Delivery Location</TableHead>
                  <TableHead>Attention Of</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {requirements.map(req => (
                  <TableRow key={req.id}>
                    <TableCell className="capitalize">{req.item_type}</TableCell>
                    <TableCell>{getSpecificationName(req.specification_id)}</TableCell>
                    <TableCell>{req.quantity}</TableCell>
                    <TableCell>
                      {req.delivery_template_id ? (
                        <button
                          onClick={() => handleViewAddress(req.delivery_template_id!)}
                          className="text-primary hover:underline flex items-center gap-1 text-left"
                        >
                          {getTemplateName(req.delivery_template_id)}
                          <Eye className="h-3 w-3 flex-shrink-0" />
                        </button>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {req.attention_of || <span className="text-muted-foreground">—</span>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <AddressDialog
          open={viewAddressDialogOpen}
          onOpenChange={setViewAddressDialogOpen}
          address={selectedTemplate}
          mode="view"
          onModeChange={() => {}}
        />
      </>
    );
  }

  // Editable view
  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            Samples and Proofs
            <Badge className="bg-orange-500/15 text-orange-600 border-orange-500/20">Optional</Badge>
          </CardTitle>
          <CardDescription>
            Specify sample and proof requirements for this job.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[120px]">Item</TableHead>
                  <TableHead className="w-[180px]">Specification</TableHead>
                  <TableHead className="w-[100px]">Quantity</TableHead>
                  <TableHead className="w-[180px]">Delivery Location</TableHead>
                  <TableHead className="w-[150px]">Attention Of</TableHead>
                  <TableHead className="w-[60px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {localRows.map((row, index) => (
                  <TableRow key={row.id || `row-${index}`}>
                    <TableCell>
                      <Select
                        value={row.item_type}
                        onValueChange={(value) => handleFieldChange(index, 'item_type', value)}
                      >
                        <SelectTrigger className="h-9">
                          <SelectValue placeholder="Select..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="proofs">Proofs</SelectItem>
                          <SelectItem value="samples">Samples</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Select
                        value={row.specification_id}
                        onValueChange={(value) => handleFieldChange(index, 'specification_id', value)}
                      >
                        <SelectTrigger className="h-9">
                          <SelectValue placeholder="Select..." />
                        </SelectTrigger>
                        <SelectContent>
                          {specifications.map(spec => (
                            <SelectItem key={spec.id} value={spec.id}>
                              {spec.specification_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        min={1}
                        value={row.quantity}
                        onChange={(e) => handleFieldChange(index, 'quantity', parseInt(e.target.value) || '')}
                        className="h-9"
                        placeholder="0"
                      />
                    </TableCell>
                    <TableCell>
                      <Select
                        value={row.delivery_template_id}
                        onValueChange={(value) => handleFieldChange(index, 'delivery_template_id', value)}
                      >
                        <SelectTrigger className="h-9">
                          <SelectValue placeholder="Select..." />
                        </SelectTrigger>
                        <SelectContent>
                          {templates.map(template => (
                            <SelectItem key={template.id} value={template.id}>
                              {template.location_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Input
                        value={row.attention_of}
                        onChange={(e) => handleFieldChange(index, 'attention_of', e.target.value)}
                        className="h-9"
                        placeholder="Recipient name"
                        autoComplete="off"
                      />
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleRemoveRow(index)}
                        disabled={localRows.length === 1 && !row.item_type}
                        className="h-9 w-9 text-muted-foreground hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <div className="flex items-center gap-3">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleAddRow}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Row
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleOpenAddressDialog}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add New Address
            </Button>
          </div>
        </CardContent>
      </Card>

      <AddressDialog
        open={addressDialogOpen}
        onOpenChange={handleAddressDialogClose}
        address={null}
        mode={addressDialogMode}
        onModeChange={setAddressDialogMode}
      />
    </>
  );
}
